import React from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Field,
    Textarea,
    Spinner,
    Text,
} from '@fluentui/react-components';
import { useCustomerCommentMutation } from '../../Api/PreparedCartEndpoints';
import { useLocalization } from '../Helpers/useLocalization';

interface ICustomerCommentDialogProps {
    cartId: string;
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

type CommentStep = 'edit' | 'saving' | 'error';

const CustomerCommentDialog: React.FC<ICustomerCommentDialogProps> = ({ cartId, open, onClose, onSuccess }) => {
    const [step, setStep] = React.useState<CommentStep>('edit');
    const [comment, setComment] = React.useState('');
    const [errorMsg, setErrorMsg] = React.useState('');
    const L = useLocalization();

    const [submitComment] = useCustomerCommentMutation();

    React.useEffect(() => {
        if (!open) {
            setStep('edit');
            setComment('');
            setErrorMsg('');
        }
    }, [open]);

    const handleSubmit = async () => {
        setStep('saving');
        const result = await submitComment({ cartId, comment });
        if ('error' in result || !result.data?.success) {
            setErrorMsg((result as any)?.data?.error ?? L('localization_cart_comment_error'));
            setStep('error');
            return;
        }
        onClose();
        onSuccess?.();
    };

    const renderContent = () => {
        switch (step) {
            case 'edit':
                return (
                    <Field label={L('localization_cart_comment_field_label')}>
                        <Textarea
                            value={comment}
                            onChange={(_, data) => setComment(data.value)}
                            placeholder={L('localization_cart_comment_placeholder')}
                            resize="vertical"
                            rows={5}
                            style={{ width: '100%' }}
                        />
                    </Field>
                );
            case 'saving':
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                        <Spinner size="small" />
                        <Text>{L('localization_cart_comment_submitting')}</Text>
                    </div>
                );
            case 'error':
                return <Text style={{ color: 'red' }}>{errorMsg}</Text>;
        }
    };

    const renderActions = () => {
        switch (step) {
            case 'edit':
                return (
                    <>
                        <Button
                            appearance="primary"
                            disabled={!comment.trim()}
                            onClick={handleSubmit}
                        >
                            {L('localization_cart_comment_submit')}
                        </Button>
                        <Button onClick={onClose}>{L('localization_common_cancel')}</Button>
                    </>
                );
            case 'error':
                return <Button onClick={onClose}>{L('localization_common_close')}</Button>;
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
            <DialogSurface style={{ minWidth: '400px' }}>
                <DialogBody>
                    <DialogTitle>{L('localization_cart_comment_dialog_title')}</DialogTitle>
                    <DialogContent style={{ padding: '1em 0' }}>
                        {renderContent()}
                    </DialogContent>
                    <DialogActions>
                        {renderActions()}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default CustomerCommentDialog;

